import { useTranslation } from 'react-i18next';
import type { WhatsAppHandoff } from '../types/events';

export interface WhatsAppHandoffBannerProps {
  handoff: WhatsAppHandoff;
  onDismiss: () => void;
}

/**
 * "Continue on WhatsApp", between the header and the transcript (CAIV-1063).
 *
 * Same slot and geometry as the error toast, so it never covers the conversation, and
 * one surface for every moment it is offered: always visible, after the assistant got
 * stuck, and after a hard error that produced no reply to attach anything to. The link
 * is absolute (the widget runs on the club's own website, where `/wh/` does not exist)
 * and resolves the club's number server-side when followed.
 */
export function WhatsAppHandoffBanner({ handoff, onDismiss }: WhatsAppHandoffBannerProps) {
  const { t } = useTranslation('widget');
  return (
    <div
      className="widget-handoff-banner"
      role="region"
      aria-label={t('handoff.title')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px 8px 16px',
        backgroundColor: '#f0fdf4',
        borderBottom: '1px solid #bbf7d0',
        flexShrink: 0,
      }}
    >
      <a
        href={handoff.url}
        target="_blank"
        rel="noopener noreferrer"
        dir="auto"
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#166534',
          textDecoration: 'none',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: '16px', lineHeight: 1 }}>&#128172;</span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '14px', fontWeight: 600 }}>
            {t('handoff.title')}
          </span>
          <span style={{ display: 'block', fontSize: '12px', color: '#15803d' }}>
            {t('handoff.hint')}
          </span>
        </span>
      </a>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('buttons.close', { ns: 'common' })}
        style={{
          flexShrink: 0,
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          borderRadius: '6px',
          color: '#15803d',
          cursor: 'pointer',
          fontSize: '18px',
          lineHeight: 1,
        }}
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  );
}
